/**
 * Firebase configuration and initialization
 */

import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { initializeFirestore, getFirestore, connectFirestoreEmulator, persistentLocalCache, persistentMultipleTabManager } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getAnalytics, type Analytics } from "firebase/analytics";
import { getAI, GoogleAIBackend } from "firebase/ai";
import { getEmulatorHost, isUsingEmulators } from './emulator';

const defaultEmulatorProjectId = 'postmortem-pheonix';

// Firebase configuration
// Note: For emulator usage, these values do not need to be real secrets.
// We provide defaults so local dev works even without a configured `.env.local`.
const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || defaultEmulatorProjectId;
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'fake-api-key-for-emulator',
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || `${projectId}.firebaseapp.com`,
  projectId,
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || `${projectId}.appspot.com`,
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '123456789',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:123456789:web:abcdef123456',
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services - MUST do auth emulator connection BEFORE calling getAuth!
let auth = null as any;
let db = null as any;
let functions = null as any;
let storage = null as any;

// Connect to emulators FIRST (before initializing services)
// NOTE: We intentionally do NOT gate on NODE_ENV.
// The Firebase Hosting emulator serves a production build, but it still must talk to emulators.
if (isUsingEmulators()) {
  try {
    const emuHost = getEmulatorHost();
    const authPort = parseInt(process.env.REACT_APP_AUTH_EMULATOR_PORT || "9099", 10);
    const firestorePort = parseInt(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT || "8081", 10);
    const functionsPort = parseInt(process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || "5001", 10);
    const storagePort = parseInt(process.env.REACT_APP_STORAGE_EMULATOR_PORT || "9199", 10);

    // Get services first
    auth = getAuth(app);
    db = getFirestore(app);
    functions = getFunctions(app);
    storage = getStorage(app);

    // Then connect to emulators
    connectAuthEmulator(auth, `http://${emuHost}:${authPort}`, { disableWarnings: true });
    connectFirestoreEmulator(db, emuHost, firestorePort);
    connectFunctionsEmulator(functions, emuHost, functionsPort);
    connectStorageEmulator(storage, emuHost, storagePort);

    console.log("✅ Connected to Firebase emulators");
    console.log(`   Auth:       http://${emuHost}:${authPort}`);
    console.log(`   Firestore:  http://${emuHost}:${firestorePort}`);
    console.log(`   Functions:  http://${emuHost}:${functionsPort}`);
    console.log(`   Storage:    http://${emuHost}:${storagePort}`);
  } catch (error: any) {
    console.error("❌ Failed to connect to Firebase emulators:", error.message);
    console.warn("   Falling back to production Firebase if available");
    // Initialize normally without emulator
    auth = getAuth(app);
    db = getFirestore(app);
    functions = getFunctions(app);
    storage = getStorage(app);
  }
} else {
  // Production - initialize Firestore with persistent cache for offline support
  auth = getAuth(app);
  try {
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch (error: any) {
    console.warn("Failed to initialize Firestore with persistent cache, falling back:", error.message);
    db = getFirestore(app);
  }
  functions = getFunctions(app);
  storage = getStorage(app);
}

// Initialize Firebase Analytics — wrapped in try/catch so failures
// (e.g. ad-blockers, SSR) never break the rest of the app.
let analytics: Analytics | null = null;
try {
  analytics = getAnalytics(app);
} catch (error: any) {
  console.warn("Failed to initialize Firebase Analytics:", error.message);
}

// Initialize Firebase AI (Gemini) — wrapped in try/catch so a failure
// here never breaks auth / Firestore / etc.
let ai: ReturnType<typeof getAI> | null = null;
try {
  ai = getAI(app, { backend: new GoogleAIBackend() });
} catch (error: any) {
  console.error("Failed to initialize Firebase AI:", error.message);
}

export { auth, db, functions, storage, analytics, ai };
export default app;
